import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { MetaDataAttributes } from 'models';
import { createFilters } from 'utils/filtering';
import Button from 'components/UI/Button';
import SelectField, { OptionType } from 'components/UI/SelectField';
import { ButtonGrid, TwoColumnGrid } from 'styles';
import { DeviceFilter, DeviceFilterAttribute } from 'models/rollout';

type Props = {
  onFilter: (value: DeviceFilter[]) => void;
  onClear: () => void;
  data: MetaDataAttributes;
};

const RolloutDeviceFilters: FC<Props> = ({ onFilter, onClear, data }) => {
  const intl = useIntl();
  const { register, handleSubmit, watch, setValue, reset } = useForm();

  // Watch
  const [updateStatus] = watch([DeviceFilterAttribute.DETAIL_STATUS]);

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const filters = createFilters<DeviceFilter>(values);
      if (filters.length) {
        onFilter(filters);
      }
    },
    [onFilter]
  );

  // Reset
  const onReset = useCallback(() => {
    reset({
      updateStatus: '',
    });
    onClear();
  }, [reset, onClear]);

  // Select option
  const onSelect = useCallback(
    (name: DeviceFilterAttribute) => (value: string) => setValue(name, value),
    [setValue]
  );

  // Parse label
  const parseOptionLabel = (langId: string, listName: string): string => {
    const name = listName.split(' ').join('_').toLowerCase();
    const id = `${langId}_${name}`;
    return `${intl.formatMessage({ id })}`;
  };

  // Status
  const statusOptions: OptionType[] = data.detailStatuses.map(({ name }) => ({
    key: name,
    label: parseOptionLabel('rollout.detail_status', name),
    value: name,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TwoColumnGrid>
        <SelectField
          name={DeviceFilterAttribute.DETAIL_STATUS}
          register={register(DeviceFilterAttribute.DETAIL_STATUS)}
          onSelect={onSelect(DeviceFilterAttribute.DETAIL_STATUS)}
          placeholder={intl.formatMessage({ id: 'select.placeholder' })}
          label={intl.formatMessage({ id: 'rollout.software_update_status' })}
          defaultValue={updateStatus}
          options={statusOptions}
        />
      </TwoColumnGrid>
      <ButtonGrid>
        <Button
          color="primary"
          backgroundColor="transparent"
          onClick={onReset}
          marginRight
        >
          <FormattedMessage id="filters.btn_clear" />
        </Button>
        <Button submit id="filter_rollouts_btn">
          <FormattedMessage id="filters.btn_submit" />
        </Button>
      </ButtonGrid>
    </form>
  );
};

export default RolloutDeviceFilters;
